import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { EntityApplication, EntityContext, EntitySummary } from '@/types/entity-context';

export type EntityContextState = {
  entities: EntitySummary[];
  current: EntityContext | null;
  applications: EntityApplication[];
  currentApplication: EntityApplication | null;
  initialized: boolean;
  loading: boolean;
  switching: boolean;
  selectionRequired: boolean;
  error: string | null;
};

export const entityContextInitialState: EntityContextState = {
  entities: [],
  current: null,
  applications: [],
  currentApplication: null,
  initialized: false,
  loading: false,
  switching: false,
  selectionRequired: false,
  error: null,
};

const slice = createSlice({
  name: 'entityContext',
  initialState: entityContextInitialState,
  reducers: {
    beginEntityBootstrap(state) {
      state.loading = true;
      state.error = null;
    },
    completeEntityBootstrap(state) {
      state.loading = false;
      state.initialized = true;
    },
    setEntities(state, { payload }: PayloadAction<EntitySummary[]>) {
      state.entities = payload;
    },
    setCurrentEntity(state, { payload }: PayloadAction<EntityContext | null>) {
      state.current = payload;
      state.selectionRequired = false;
    },
    setEntityApplications(state, { payload }: PayloadAction<EntityApplication[]>) {
      state.applications = payload;
    },
    setCurrentEntityApplication(state, { payload }: PayloadAction<EntityApplication | null>) {
      state.currentApplication = payload;
      if (payload) {
        const index = state.applications.findIndex((item) => item.id === payload.id);
        if (index >= 0) state.applications[index] = payload;
        else state.applications.unshift(payload);
      }
    },
    setEntitySelectionRequired(state, { payload }: PayloadAction<boolean>) {
      state.selectionRequired = payload;
    },
    setEntitySwitching(state, { payload }: PayloadAction<boolean>) {
      state.switching = payload;
    },
    setEntityContextError(state, { payload }: PayloadAction<string | null>) {
      state.error = payload;
      state.loading = false;
      state.switching = false;
      state.initialized = true;
    },
    resetEntityContext() {
      return entityContextInitialState;
    },
  },
});

export const {
  beginEntityBootstrap,
  completeEntityBootstrap,
  setEntities,
  setCurrentEntity,
  setEntityApplications,
  setCurrentEntityApplication,
  setEntitySelectionRequired,
  setEntitySwitching,
  setEntityContextError,
  resetEntityContext,
} = slice.actions;

export default slice.reducer;
